import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronBack, IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { signupUser } from '../../services/authService';
import ieumLogo from '../../assets/ieum-logo.png';

// ───────── styled ─────────
const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bgWhite};
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.bgWhite};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  z-index: 10;
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const HeaderTitle = styled.h1`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin: 0 0 0 ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text};
`;

const Content = styled.main`
  flex: 1;
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.realtor.pagePadding} 60px;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const LogoImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
`;

const HeroTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: 1.4;
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

const HeroBlue = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const HeroSub = styled.p`
  margin: ${({ theme }) => theme.spacing.sm} 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const FeatureCard = styled.div`
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.base};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:first-child { padding-top: ${({ theme }) => theme.spacing.xs}; }
  &:last-child { padding-bottom: ${({ theme }) => theme.spacing.xs}; border-bottom: 0; }
`;

const FeatureTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const FeatureDesc = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const KakaoBtn = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: #FEE500;
  color: #3C1E1E;
  border: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.xxl};

  &:disabled {
    opacity: 0.85;
    cursor: not-allowed;
  }
`;

const KakaoIcon = styled.svg`
  flex-shrink: 0;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
  margin: ${({ theme }) => theme.spacing.xl} 0;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '48px' : '16px')};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  background: ${({ theme }) => theme.colors.bgWhite};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textLight};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const EyeButton = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  padding: 4px;
  cursor: pointer;
`;

const ErrorMsg = styled.div`
  background: #fef2f2;
  color: ${({ theme }) => theme.colors.danger};
  border: 1px solid #fecaca;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 10px 12px;
  font-size: 12px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

/* ── 약관 영역 ── */
const TermsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const AllCheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const CustomCheckbox = styled.span`
  width: 22px;
  height: 22px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 2px solid ${({ checked, theme }) => checked ? theme.colors.primary : theme.colors.border};
  background: ${({ checked, theme }) => checked ? theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;

  &::after {
    content: '';
    width: 6px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: ${({ checked }) => checked ? 'block' : 'none'};
    margin-top: -1px;
  }
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`;

const TermItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
`;

const TermItemLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const TermViewLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  flex-shrink: 0;
  white-space: nowrap;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 0;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled, theme }) => (disabled ? theme.colors.border : theme.colors.primary)};
  color: ${({ disabled, theme }) => (disabled ? theme.colors.textLight : '#fff')};
  transition: background 0.15s;
  margin-top: ${({ theme }) => theme.spacing.xxl};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const BottomLine = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
  font-size: ${({ theme }) => theme.fontSize.base};
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  padding-bottom: ${({ theme }) => theme.spacing.xl};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    margin-left: 8px;
    text-decoration: underline;
  }
`;

// ───────── util ─────────
const formatPhone = (v) => {
  const digits = v.replace(/\D/g, '').slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

const validateId = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const validatePw = (v) =>
  v.length >= 8 && /[A-Za-z]/.test(v) && /\d/.test(v) && /[^A-Za-z0-9]/.test(v);
const validatePhone = (v) => /^010-\d{4}-\d{4}$/.test(v);

// ───────── component ─────────
const SignupPage = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    id: '',
    password: '',
    passwordConfirm: '',
    name: '',
    phone: '',
    business_name: '',
  });

  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const [terms, setTerms] = useState({
    all_required: false,
    service: false,
    privacy: false,
    third_party: false,
    marketing: false,
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const requiredAllChecked = terms.service && terms.privacy && terms.third_party;
  const allChecked = requiredAllChecked && terms.marketing;

  const handleField = (key) => (e) => {
    const value = key === 'phone' ? formatPhone(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleAll = () => {
    const next = !allChecked;
    setTerms({
      all_required: next,
      service: next,
      privacy: next,
      third_party: next,
      marketing: next,
    });
  };

  const toggleMasterRequired = () => {
    const next = !(terms.all_required && requiredAllChecked);
    setTerms((prev) => ({
      ...prev,
      all_required: next,
      service: next,
      privacy: next,
      third_party: next,
    }));
  };

  const toggleTerm = (key) => () => {
    setTerms((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      // Sync master required checkbox
      updated.all_required = updated.service && updated.privacy && updated.third_party;
      return updated;
    });
  };

  const canSubmit =
    validateId(form.id) &&
    validatePw(form.password) &&
    form.password === form.passwordConfirm &&
    form.name.trim().length > 0 &&
    validatePhone(form.phone) &&
    form.business_name.trim().length > 0 &&
    requiredAllChecked &&
    !loading;

  const showToast = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) { showToast('이름을 입력해 주세요.'); return; }
    if (!validateId(form.id)) { showToast('올바른 이메일 형식으로 입력해 주세요.'); return; }
    if (!validatePw(form.password)) { showToast('비밀번호는 영문, 숫자, 기호를 포함해 8자 이상이어야 합니다.'); return; }
    if (form.password !== form.passwordConfirm) { showToast('비밀번호가 일치하지 않습니다.'); return; }
    if (!validatePhone(form.phone)) { showToast('휴대폰번호 형식이 올바르지 않습니다.'); return; }
    if (!form.business_name.trim()) { showToast('부동산 중개 사무소명을 입력해 주세요.'); return; }
    if (!requiredAllChecked) { showToast('필수 약관에 모두 동의해 주세요.'); return; }

    setLoading(true);
    try {
      await signupUser({
        email: form.id.trim(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone,
        businessName: form.business_name.trim(),
        role: 'realtor',
      });
      alert('가입이 완료되었습니다. 관리자 승인 후 로그인 가능합니다.');
      navigate('/login');
    } catch (err) {
      if (err?.code === 'auth/id-already-in-use') {
        setError('이미 사용 중인 아이디입니다.');
      } else {
        setError(err?.message || '회원가입에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Header>
        <HeaderInner>
          <BackBtn type="button" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <IoChevronBack size={22} />
          </BackBtn>
          <HeaderTitle>회원가입</HeaderTitle>
        </HeaderInner>
      </Header>

      <Content>
        {/* 브랜드 + 히어로 */}
        <BrandRow>
          <LogoImg src={ieumLogo} alt="이음" />
        </BrandRow>
        <HeroTitle>
          이음과 함께<br />
          <HeroBlue>스마트하게 시작하세요</HeroBlue>
        </HeroTitle>
        <HeroSub>
          고객·매물 관리를 한 곳에서!{'\n'}가입 후 바로 서비스를 이용해 보세요.
        </HeroSub>

        {/* 특장점 */}
        <FeatureCard>
          <FeatureItem>
            <FeatureTitle>간단한 사용법</FeatureTitle>
            <FeatureDesc>중개 업무에 맞춘 흐름으로 빠르게 익숙해지실 수 있어요.</FeatureDesc>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>최고의 안정성</FeatureTitle>
            <FeatureDesc>약관과 개인정보 처리 기준을 투명하게 안내합니다.</FeatureDesc>
          </FeatureItem>
          <FeatureItem>
            <FeatureTitle>성장 인사이트 제공</FeatureTitle>
            <FeatureDesc>데이터 기반으로 업무 효율을 높일 수 있어요.</FeatureDesc>
          </FeatureItem>
        </FeatureCard>

        {/* 카카오 버튼 */}
        <KakaoBtn type="button" disabled>
          <KakaoIcon viewBox="0 0 24 24" width="20" height="20">
            <path d="M12 3C6.48 3 2 6.36 2 10.44c0 2.61 1.74 4.9 4.36 6.2-.14.52-.9 3.34-.93 3.55 0 0-.02.16.08.22.1.06.22.03.22.03.3-.04 3.44-2.26 3.98-2.64.74.1 1.52.16 2.3.16 5.52 0 10-3.36 10-7.52S17.52 3 12 3z" fill="#3C1E1E"/>
          </KakaoIcon>
          카카오로 3초만에 가입하기
        </KakaoBtn>

        <Divider>직접 입력하여 가입</Divider>

        {/* 가입 폼 */}
        <form onSubmit={handleSubmit}>
          <Field>
            <Label>이메일</Label>
            <Input
              type="text"
              placeholder="example@email.com"
              value={form.id}
              onChange={handleField('id')}
              autoComplete="username"
            />
          </Field>

          <Field>
            <Label>비밀번호</Label>
            <InputWrap>
              <Input
                type={showPw ? 'text' : 'password'}
                placeholder="영문자, 숫자, 기호 포함 8자 이상"
                value={form.password}
                onChange={handleField('password')}
                autoComplete="new-password"
                $hasIcon
              />
              <EyeButton type="button" onClick={() => setShowPw((v) => !v)}>
                {showPw ? <IoEyeOffOutline size={20} /> : <IoEyeOutline size={20} />}
              </EyeButton>
            </InputWrap>
          </Field>

          <Field>
            <Label>비밀번호 확인</Label>
            <InputWrap>
              <Input
                type={showPw2 ? 'text' : 'password'}
                placeholder="영문자, 숫자, 기호 포함 8자 이상"
                value={form.passwordConfirm}
                onChange={handleField('passwordConfirm')}
                autoComplete="new-password"
                $hasIcon
              />
              <EyeButton type="button" onClick={() => setShowPw2((v) => !v)}>
                {showPw2 ? <IoEyeOffOutline size={18} /> : <IoEyeOutline size={18} />}
              </EyeButton>
            </InputWrap>
          </Field>

          <Field>
            <Label>이름</Label>
            <Input
              type="text"
              placeholder="홍길동"
              value={form.name}
              onChange={handleField('name')}
            />
          </Field>

          <Field>
            <Label>휴대폰 번호</Label>
            <Input
              type="tel"
              placeholder="010-1234-5678"
              value={form.phone}
              onChange={handleField('phone')}
            />
          </Field>

          <Field>
            <Label>공인중개 사무소</Label>
            <Input
              type="text"
              placeholder="000 부동산"
              value={form.business_name}
              onChange={handleField('business_name')}
            />
          </Field>

          {/* 약관 동의 */}
          <TermsSection>
            {/* 전체 동의 */}
            <AllCheckRow>
              <HiddenCheckbox type="checkbox" checked={allChecked} onChange={toggleAll} />
              <CustomCheckbox checked={allChecked} />
              전체 동의
            </AllCheckRow>

            {/* [필수] 만 14세 이상 */}
            <TermItemRow>
              <TermItemLabel>
                <HiddenCheckbox
                  type="checkbox"
                  checked={terms.all_required}
                  onChange={toggleMasterRequired}
                />
                <CustomCheckbox checked={terms.all_required} />
                [필수] 만 14세 이상이며 모두 동의합니다.
              </TermItemLabel>
            </TermItemRow>

            {/* 개별 필수 약관 */}
            <TermItemRow>
              <TermItemLabel>
                <HiddenCheckbox
                  type="checkbox"
                  checked={terms.service}
                  onChange={toggleTerm('service')}
                />
                <CustomCheckbox checked={terms.service} />
                [필수] 이용약관
              </TermItemLabel>
              <TermViewLink to="/terms/service">전체보기</TermViewLink>
            </TermItemRow>

            <TermItemRow>
              <TermItemLabel>
                <HiddenCheckbox
                  type="checkbox"
                  checked={terms.privacy}
                  onChange={toggleTerm('privacy')}
                />
                <CustomCheckbox checked={terms.privacy} />
                [필수] 개인 정보처리방침
              </TermItemLabel>
              <TermViewLink to="/terms/privacy">전체보기</TermViewLink>
            </TermItemRow>

            <TermItemRow>
              <TermItemLabel>
                <HiddenCheckbox
                  type="checkbox"
                  checked={terms.third_party}
                  onChange={toggleTerm('third_party')}
                />
                <CustomCheckbox checked={terms.third_party} />
                [필수] 개인 정보 제3자 제공동의
              </TermItemLabel>
              <TermViewLink to="/terms/third_party">전체보기</TermViewLink>
            </TermItemRow>

            {/* 선택 약관 */}
            <TermItemRow>
              <TermItemLabel>
                <HiddenCheckbox
                  type="checkbox"
                  checked={terms.marketing}
                  onChange={toggleTerm('marketing')}
                />
                <CustomCheckbox checked={terms.marketing} />
                [선택] 광고성 정보 수신에 모두 동의합니다.
              </TermItemLabel>
            </TermItemRow>
          </TermsSection>

          {error && <ErrorMsg>{error}</ErrorMsg>}
          <SubmitBtn type="submit" disabled={loading}>
            {loading ? '가입 처리 중...' : '가입 완료'}
          </SubmitBtn>
        </form>

        <BottomLine>
          이미 계정이 있으신가요?
          <Link to="/login">로그인하기</Link>
        </BottomLine>
      </Content>
    </Page>
  );
};

export default SignupPage;
